import { css } from '@emotion/css';
import { UsageTrackerEvent, usageTracker } from '@volumental/analytic';
import { Spinner } from '@volumental/common';
import { type JSX, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CameraInstructionModal } from '../camera-instructor';
import ImageLoadView from '../image-load-view';
import { CameraError } from './camera-error';
import { CameraOverlay } from './overlay/camera-overlay';
import { type Overlay } from './overlay/camera-overlay-types';
import { CameraButtonShutter } from './shutter';
import { useUserMedia } from './user-media';

const { DEV } = import.meta.env;

const container = css`
  user-select: none;
  position: relative;
  width: 100%;
  height: 100%;
  background-color: rgba(50, 50, 50, 0.3);
`;

const videoStyles = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const shutterOverlay = css`
  position: absolute;
  left: 50%;
  bottom: 50px;
  transform: translate(-50%, 0);
  margin: 0 auto;
  user-select: none;
  outline: none;
`;

const debugOverlay = css`
  position: absolute;
  top: 15px;
  left: 15px;
  padding: 5px;
  color: #fff;
  width: 300px;
  background-color: rgba(50, 50, 50, 0.3);
`;

const spinnerContainer = css`
  position: absolute;
  left: 50%;
  bottom: 50%;
  transform: translate(-50%, 50%);
  margin: 0 auto;
`;

const defaultImageSize = {
  width: 1440,
  height: 1920,
};

const takePhoto = async (
  track: MediaStreamTrack,
  videoElem: HTMLVideoElement,
): Promise<Blob> => {
  if (!hasImageCapture()) {
    return takePhotoCanvas(track, videoElem);
  }
  const capture = new ImageCapture(track);
  return capture.takePhoto({
    imageWidth: defaultImageSize.height,
    imageHeight: defaultImageSize.width,
  });
};

const hasImageCapture = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return 'ImageCapture' in window;
};

const takePhotoCanvas = async (
  track: MediaStreamTrack,
  videoElem: HTMLVideoElement,
): Promise<Blob> => {
  const canvas = document.createElement('canvas');

  const { width, height } = track.getSettings();
  canvas.width = width || defaultImageSize.width;
  canvas.height = height || defaultImageSize.height;
  canvas.getContext('2d')?.drawImage(videoElem, 0, 0);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b == null) {
          reject(new Error('Could not take photo'));
        } else {
          resolve(b);
        }
      },
      'image/jpeg',
      1,
    );
  });
};

interface CameraProps {
  overlay?: Overlay;
  onImage: (blob: Blob) => void;
}

export function Camera({
  overlay,
  onImage = () => {},
}: CameraProps): JSX.Element {
  const { t } = useTranslation();
  const [video, setVideo] = useState<HTMLVideoElement>();
  const [showHelp, setShowHelp] = useState(false);
  const { stream, error, askPermission } = useUserMedia();

  const [devFile, setDevFile] = useState<File>();
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photoFailed, setPhotoFailed] = useState<boolean>(false);

  const videoRef = useCallback(
    (node: HTMLVideoElement) => {
      if (node != null && stream) {
        node.srcObject = stream;
        setVideo(node);
      } else if (node != null) {
        node.srcObject = null;
        setVideo(node);
      }
    },
    [stream],
  );

  const onClick = (): void => {
    if (devFile) {
      onImage(devFile as Blob);
      return;
    }

    if (video && stream) {
      setLoading(true);
      const track = stream.getTracks()[0];
      takePhoto(track, video)
        .then((photo) => {
          onImage(photo);
        })
        .catch((error) => {
          usageTracker.track(UsageTrackerEvent.CameraPhotoError, { error });
          setPhotoFailed(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const selectFile = (files: FileList | null): void => {
    if (files && files.length > 0) {
      setDevFile(files[0]);
    }
  };

  useEffect(() => {
    if (error) {
      usageTracker.track(UsageTrackerEvent.CameraLoadingError, {
        error_name: error.name,
        error_message: error.message,
      });
    }
  }, [error]);
  if (error) {
    return (
      <>
        <CameraError
          title={t('cameraPermission.title')}
          onClick={() => {
            setShowHelp(true);
          }}
          callToAction={t('cameraPermission.buttonTitle')}
        />
        <CameraInstructionModal
          show={showHelp}
          onClose={() => {
            setShowHelp(false);
            askPermission();
          }}
        />
      </>
    );
  }

  if (photoFailed) {
    return (
      <CameraError
        title={t('cameraPermission.photoFailed')}
        onClick={() => {
          setPhotoFailed(false);
        }}
        callToAction={t('button.tryAgain')}
      />
    );
  }

  return (
    <div className={container}>
      <video
        className={videoStyles}
        controls={false}
        muted
        autoPlay
        playsInline
        onLoadedMetadata={() => {
          setReady(true);
        }}
        ref={videoRef}
      />
      {overlay && <CameraOverlay variant={overlay} />}
      {(stream && ready) || devFile ? (
        <div className={shutterOverlay}>
          <CameraButtonShutter onClick={onClick} />
        </div>
      ) : (
        <div className={spinnerContainer}>
          <Spinner size="52px" />
        </div>
      )}
      {DEV && (
        <div className={debugOverlay}>
          <input
            aria-label="file input"
            type="file"
            onChange={(e) => {
              selectFile(e.target.files);
            }}
          />
          <p>{stream?.getVideoTracks()[0].label}</p>
          <p>{stream?.getVideoTracks()[0].getSettings().width}</p>
          <p>{stream?.getVideoTracks()[0].getSettings().height}</p>
        </div>
      )}
      {loading && <ImageLoadView />}
    </div>
  );
}
